/**
 * useFeatureFlags Hook
 * 
 * Fetches and provides feature flag states from the backend.
 * Used to conditionally show/hide features in the user frontend.
 * 
 * Features controlled:
 * - leaderboard_enabled: Ranks page and Leaderboard button
 * - maps_enabled: Map button and Explore Map button  
 * - inbox_enabled: Inbox button in bottom nav
 */

import { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../utils/constants';

// Default flags (all enabled - safe default)
const DEFAULT_FLAGS = {
  leaderboard_enabled: true,
  maps_enabled: true,
  inbox_enabled: true
};

// Cache for feature flags (shared across hook instances)
let flagsCache = null;
let lastFetchTime = 0;
const CACHE_TTL = 60000; // 1 minute cache TTL

export const useFeatureFlags = () => {
  const [flags, setFlags] = useState(flagsCache || DEFAULT_FLAGS);
  const [loading, setLoading] = useState(!flagsCache);
  const [error, setError] = useState(null);

  const fetchFlags = useCallback(async (force = false) => {
    // Use cache if valid
    const now = Date.now();
    if (!force && flagsCache && (now - lastFetchTime) < CACHE_TTL) {
      setFlags(flagsCache);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/feature-flags`);
      const data = await response.json();
      
      if (data.success && data.flags) {
        flagsCache = data.flags;
        lastFetchTime = now;
        setFlags(data.flags);
        setError(null);
      } else {
        // Use defaults on error
        setFlags(DEFAULT_FLAGS);
      }
    } catch (err) {
      console.warn('Failed to fetch feature flags, using defaults:', err.message);
      setError(err.message);
      // Use defaults on error - don't break the app
      setFlags(DEFAULT_FLAGS);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch flags on mount
  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  // Convenience getters
  const isLeaderboardEnabled = flags.leaderboard_enabled ?? true;
  const isMapsEnabled = flags.maps_enabled ?? true;
  const isInboxEnabled = flags.inbox_enabled ?? true;

  return {
    flags,
    loading,
    error,
    refetch: () => fetchFlags(true),
    // Convenience accessors
    isLeaderboardEnabled,
    isMapsEnabled,
    isInboxEnabled
  };
};

export default useFeatureFlags;
